import { Component, computed, input } from '@angular/core';

type LanguageCountMap = { [language: string]: number };
type LanguageEntry = { language: string, count: number, percentage: number };

@Component({
    selector: 'language-match-distribution-progress-bar',
    templateUrl: './language-distribution-progress-bar.component.html',
    standalone: false,
})
export class LanguageDistributionProgressBarComponent {
    detectedLanguages = input<LanguageCountMap>();
    declaredLanguages = input<LanguageCountMap>();
    totalPages = input<number>(0);
    totalPagesWithTags = input<number>(0);

    detectedTotalLanguages = computed<number>(() => {
        const detectedLanguages = this.detectedLanguages();
        if (!detectedLanguages) {
            return 0;
        }

        return Object.values(detectedLanguages).reduce((sum, count) => sum + count, 0);
    });

    detectedLanguageEntries = computed<LanguageEntry[]>(() => {
        const detectedLanguages = this.detectedLanguages();
        const detectedTotalLanguages = this.detectedTotalLanguages();
        if (!detectedLanguages || detectedTotalLanguages === 0) {
            return [];
        }

        return Object.entries(detectedLanguages).map(([language, count]) => ({
            language,
            count: count as number,
            percentage: ((count as number) / detectedTotalLanguages) * 100,
        })).sort((a, b) => b.count - a.count);
    });

    declaredLanguageEntries = computed<LanguageEntry[]>(() => {
        if (this.totalPages() === 0) {
            return [];
        }

        const totalPages = this.totalPages();
        const entries: LanguageEntry[] = [];

        // Add entries for declared languages
        const declaredLanguages = this.declaredLanguages();
        if (declaredLanguages) {
            Object.entries(declaredLanguages).forEach(([language, count]) => {
                entries.push({
                    language,
                    count: count as number,
                    percentage: ((count as number) / totalPages) * 100,
                });
            });
        }

        entries.sort((a, b) => b.count - a.count);

        const notDeclaredCount = totalPages - this.totalDeclaredLanguages();
        if (notDeclaredCount > 0) {
            const notDeclaredEntry = entries.find(entry => entry.language === '');
            if (notDeclaredEntry) {
                notDeclaredEntry.count += notDeclaredCount;
                notDeclaredEntry.percentage = (notDeclaredEntry.count / totalPages) * 100;
            } else {
                entries.push({
                    language: '', // Empty string to indicate "Not declared"
                    count: notDeclaredCount,
                    percentage: (notDeclaredCount / totalPages) * 100,
                });
            }
        }

        return entries;
    });

    totalDetectedLanguages = computed(() => {
        const detectedLanguages = this.detectedLanguages();
        return detectedLanguages ? Object.keys(detectedLanguages).length : 0;
    });

    totalUniqueDeclaredLanguages = computed(() => {
        const declaredLanguages = this.declaredLanguages();
        return declaredLanguages ? Object.keys(declaredLanguages).length : 0;
    });

    totalDeclaredLanguages = computed(() => {
        const declaredLanguages = this.declaredLanguages();
        if (!declaredLanguages) {
            return 0;
        }

        return Object.values(declaredLanguages).reduce((sum, count) => sum + count, 0);
    });

    totalPagesWithoutTags = computed(() => {
        return this.totalPages() - (this.totalPagesWithTags() || 0);
    });

    getLanguageColor (index: number): string {
        const colors = [
            '#007bff', // Blue
            '#28a745', // Green
            '#dc3545', // Red
            '#ffc107', // Yellow
            '#17a2b8', // Cyan
            '#6f42c1', // Purple
            '#fd7e14', // Orange
            '#20c997', // Teal
            '#e83e8c', // Pink
        ];
        return colors[index % colors.length];
    }
}
